/**
 * views/reporting/sections/PmFocus.jsx - margin by customer, quote-to-PO
 * and the stalest bids.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, Bar, Cell } from 'recharts';
import { Icon } from '../../../components/ui/Icon';
import { fmt } from '../../../utils/format';
import { exportCsv } from '../../../utils/exportCsv';
import { CSV_BTN } from '../reportingHelpers';
import { CARD, TICK, TT } from '../reportingTokens';
import { SectionHeader } from '../SectionHeader';

export function renderPmFocus({ yearLabel, marginByCustomer, quoteToPoStats, openBidAging, onNavigate }) {
  return (
    <>
      {/* PM-FOCUS: Margin by Customer + Quote-to-PO + Stalest Bids */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr', gap: 12, marginBottom: 20 }}>
        {/* Margin by Customer */}
        <div>
          <SectionHeader
            title="Margin by Customer (Won)"
            subtitle={`Top 8 · ${yearLabel} · only rows with recorded labor cost`}
            explain={{
              title: 'Margin by Customer',
              body: "Average gross margin on won deals per customer. Higher is better — GAT target is ≥30%. Only includes won rows where labor cost was actually recorded so historical uncosted rows can't inflate the number to 100%.",
              formula: 'Σ(price - cogs) / Σ(price)\n  for status="Won" & labor_cost > 0\n  grouped by customer',
            }}
            right={
              <button
                onClick={() =>
                  exportCsv(
                    'margin_by_customer.csv',
                    marginByCustomer.map((d) => ({ Customer: d.name, 'Avg Margin %': d.margin, Deals: d.n }))
                  )
                }
                style={CSV_BTN}
              >
                <Icon name="download" size={10} color="#666" /> CSV
              </button>
            }
          />
          <div style={{ ...CARD, height: 290 }}>
            {marginByCustomer.length === 0 ? (
              <div style={{ color: '#8A8A8A', fontSize: 11, textAlign: 'center', padding: 40 }}>No won-with-cost data for this period</div>
            ) : (
              <div style={{ height: Math.max(250, marginByCustomer.length * 30) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={marginByCustomer} layout="vertical" barSize={16} margin={{ top: 10, right: 50, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={TICK}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 'dataMax + 5']}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      interval={0}
                      tick={{ ...TICK, fontSize: 10.5, fill: '#0D0D0D', fontWeight: 700 }}
                      axisLine={false}
                      tickLine={false}
                      width={170}
                    />
                    <Tooltip
                      contentStyle={TT}
                      formatter={(v, n, p) => [`${v}% (${p.payload.n} deal${p.payload.n > 1 ? 's' : ''})`, 'Margin']}
                      cursor={{ fill: '#F4F4F4' }}
                    />
                    <ReferenceLine
                      x={30}
                      stroke="#1B5E20"
                      strokeDasharray="3 3"
                      label={{ value: 'Target 30%', position: 'top', fontSize: 8.5, fill: '#1B5E20', fontWeight: 700 }}
                    />
                    <Bar
                      dataKey="margin"
                      radius={[0, 6, 6, 0]}
                      label={{ position: 'right', formatter: (v) => `${v}%`, fontSize: 10, fontWeight: 800, fill: '#444' }}
                    >
                      {marginByCustomer.map((m, i) => (
                        <Cell key={i} fill={m.margin >= 30 ? '#1B5E20' : m.margin >= 20 ? '#E65100' : '#B71C1C'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Quote → PO Turnaround KPIs */}
        <div>
          <SectionHeader
            title="Quote → PO Turnaround"
            subtitle={`How fast won bids close · ${yearLabel}`}
            explain={{
              title: 'Quote → PO Turnaround',
              body: 'For every won deal that has both a quote date and a PO issue date, how many days passed between them. Faster = better — shorter cycle means tighter sales-engineering ↔ customer interaction.',
              formula: 'po_date_issued - quote_date\n  for status="Won"\n  (deals missing either date are skipped)',
            }}
          />
          <div style={{ ...CARD, height: 290, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
            {!quoteToPoStats ? (
              <div style={{ color: '#8A8A8A', fontSize: 11, textAlign: 'center' }}>No won deals with both quote + PO dates</div>
            ) : (
              <>
                <div style={{ textAlign: 'center', padding: '0 8px' }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: '#5A5A5A',
                      textTransform: 'uppercase',
                      letterSpacing: 1.5,
                      marginBottom: 4,
                    }}
                  >
                    Average
                  </div>
                  <div style={{ fontSize: 56, fontWeight: 900, color: '#1B5E20', lineHeight: 1 }}>{quoteToPoStats.avg}</div>
                  <div style={{ fontSize: 12, color: '#777', fontWeight: 600, marginTop: 4 }}>days from quote to PO</div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 8,
                    padding: '8px 16px',
                    borderTop: '1px solid #F0F0F0',
                    marginTop: 8,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#0D0D0D' }}>{quoteToPoStats.median}d</div>
                    <div style={{ fontSize: 9, color: '#5A5A5A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Median
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#1B5E20' }}>{quoteToPoStats.fast}</div>
                    <div style={{ fontSize: 9, color: '#5A5A5A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      ≤14 days
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#B71C1C' }}>{quoteToPoStats.slow}</div>
                    <div style={{ fontSize: 9, color: '#5A5A5A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                      {'>'}60 days
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: '#5A5A5A', textAlign: 'center', marginTop: 4 }}>
                  {quoteToPoStats.count} sampled won deals
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stalest open bids — table */}
        <div>
          <SectionHeader
            title="Stalest Open Bids"
            subtitle={`Quotes sitting longest · ${yearLabel}`}
            explain={{
              title: 'Stalest Open Bids',
              body: 'Top 12 Open bids ranked by age (days since quote). Red bars are >90 days; these usually need re-pricing or a customer follow-up.',
              formula: 'days = today - quote_date · sort DESC · limit 12',
            }}
          />
          <div style={{ ...CARD, height: 290, padding: 0 }}>
            {openBidAging.length === 0 ? (
              <div style={{ color: '#8A8A8A', fontSize: 11, textAlign: 'center', padding: 40 }}>No open bids in this period</div>
            ) : (
              <div style={{ overflowY: 'auto', height: 290, padding: '4px 0' }}>
                {openBidAging.map((b, i) => {
                  const color = b.days > 90 ? '#B71C1C' : b.days > 60 ? '#E65100' : b.days > 30 ? '#1565C0' : '#1B5E20';
                  // Deep-link smartly: Open goes to Opportunities, everything
                  // else (Won/Lost/Cancelled, which shouldn't be in this list anyway)
                  // goes to Projects.
                  const status = (b.status || '').toLowerCase();
                  const target = status === 'open' ? 'opportunities' : 'projects';
                  const handleOpen = () => onNavigate && onNavigate(target, { project: b.id });
                  const dateLabel = (() => {
                    if (!b.quoteDate) return null;
                    const m = String(b.quoteDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
                    return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}` : b.quoteDate;
                  })();
                  return (
                    <div
                      key={b.id || i}
                      onClick={handleOpen}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F1F8E9')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        borderBottom: '1px solid #F5F5F5',
                        fontFamily: 'Montserrat',
                        cursor: onNavigate ? 'pointer' : 'default',
                        transition: 'background 0.12s',
                      }}
                      title="Open in Projects"
                    >
                      <span style={{ width: 44, fontSize: 13, fontWeight: 900, color, textAlign: 'right' }}>{b.days}d</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#0D0D0D',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {b.name}
                        </div>
                        <div
                          style={{ fontSize: 9.5, color: '#666', fontWeight: 600, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}
                        >
                          <span>{b.customer}</span>
                          {b.quoteLetter && (
                            <span style={{ fontFamily: 'JetBrains Mono, Consolas, monospace', color: '#1B5E20' }}>· #{b.quoteLetter}</span>
                          )}
                          {dateLabel && <span>· {dateLabel}</span>}
                          <span style={{ color: '#0D0D0D', fontWeight: 700 }}>· {fmt(b.value)}</span>
                        </div>
                      </div>
                      {onNavigate && <Icon name="arrow" size={12} color="#ccc" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
